/**
 * Property Table Component with Bulk Actions
 * Story-3: Extended filters and sorting in property search
 * MVP: Property table integration with bulk actions
 */

"use client";

import { useCallback, useMemo, useState } from "react";

import { Chip, Image } from "@nextui-org/react";
import { useTranslations } from "next-intl";

// Icons
import { PAGINATION_CONFIG } from "@/config/search-filters.config";
import UserCircleIcon from "@/icons/ui/feedback/user-circle.svg";
import WarningCircleIcon from "@/icons/ui/feedback/warning-circle.svg";
// Loader component for loading states
// Local imports
import { bulkActionsService } from "@/services/bulkActions.service";
import {
  propertyService,
  PropertyTableItem,
} from "@/services/property.service";
import { useCurrencyStore } from "@/store/currency.store";
import {
  BulkAction,
  BulkActionsPanel,
  PropertyTableColumn,
  useBulkActions,
  VirtualizedPropertyTable,
} from "@/templates";
// Note: ExclusiveType is now server-synced via usePropertyOptions
import { Amount, AmountVariant } from "@/ui/Common/Amount";
import Loader from "@/ui/Common/Loader";
import { formatArea } from "@/utils/formatArea";
import { formatDate } from "@/utils/formatDate";
import { RealtorContextMenu } from "@/widgets/Realtor/RealtorContextMenu";

import { FigmaPagination } from "./FigmaPagination";

// Property interface for type safety
interface Property {
  id?: string;
  _id?: string;
  customId?: string;
  mainInfo?: {
    title?: string;
    area?: number;
    rooms?: number;
  };
  location?: {
    address?: string;
    country?: string;
    region?: string;
    city?: string;
    district?: string;
    microdistrict?: string;
  };
  pricing?: {
    totalPrice?: number;
    pricePerSqM?: number;
    currency?: string;
    convertedPricesMin?: number;
    convertedPricesMax?: number;
  };
  media?: {
    photos?: Array<{ url: string }>;
  };
  images?: any[]; // Real API field for images
  additionalInfo?: {
    floor?: number;
    floors?: number;
  };
  realtorFields?: {
    callDate?: string;
    callStatus?: string;
    exclusiveType?: ExclusiveType;
    assignedRealtor?: {
      name?: string;
    };
    ownerPhone?: string;
  };
  creatorFields?: {
    phone?: string;
  };
  publicationStatus?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface PropertyTableProps {
  properties: PropertyTableItem[];
  loading?: boolean;
  currentSort: string;
  onSortChange: (sort: string) => void;
  pagination: {
    currentPage: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
  };
  className?: string;
  // Bulk actions - handled internally
  enableBulkActions?: boolean;
  // Control where pagination is rendered. When true, external pager will be used.
  hideInternalPagination?: boolean;
}

export function PropertyTable({
  properties,
  loading = false,
  currentSort,
  onSortChange,
  pagination,
  className,
  enableBulkActions = true,
  hideInternalPagination = false,
}: PropertyTableProps) {
  const t = useTranslations();
  const tSearch = useTranslations();
  const tBulk = useTranslations("realtor");
  const tCall = useTranslations("realtor");
  const { currency } = useCurrencyStore();
  const [isExecutingAction, setIsExecutingAction] = useState(false);

  // Fallback helper: if translation returns a dotted key (missing), use fallback
  const safeText = useCallback((value: any, fallback: string): string => {
    if (value == null) return fallback;
    const str = String(value);
    return str.includes(".") ? fallback : str;
  }, []);

  // Property key extractor
  const getPropertyKey = useCallback((property: PropertyTableItem) => {
    const PropertyService = propertyService.constructor as any;
    return PropertyService.getPropertyKey(property);
  }, []);

  // Bulk actions configuration
  const bulkActions: BulkAction[] = useMemo(() => {
    // Exactly 4 actions in the panel (per Figma): actualize, export, approve, assign
    const actions: BulkAction[] = [
      {
        id: "actualize",
        label: tSearch("search.table.bulk.action.actualize") || "Actualize",
      },
      {
        id: "export",
        label: tSearch("search.table.bulk.action.export") || "Export",
      },
      {
        id: "approve",
        label: tSearch("search.table.bulk.action.approve") || "Approve",
      },
      {
        id: "assign",
        label: tSearch("search.table.bulk.action.assign") || "Assign",
      },
    ];

    return actions;
  }, [tSearch]);

  // Handle bulk actualize action
  const handleBulkActualize = useCallback(async (propertyIds: string[]) => {
    setIsExecutingAction(true);
    try {
      return await bulkActionsService.actualize({
        propertyIds,
        updatePublicationDate: true,
      });
    } finally {
      setIsExecutingAction(false);
    }
  }, []);

  // Handle bulk archive action
  const handleBulkArchive = useCallback(async (propertyIds: string[]) => {
    setIsExecutingAction(true);
    try {
      return await bulkActionsService.archive({
        propertyIds,
      });
    } finally {
      setIsExecutingAction(false);
    }
  }, []);

  // Bulk actions hook with real service integration
  const bulkActionsHook = useBulkActions({
    items: properties,
    getItemKey: getPropertyKey,
    actions: {
      actualize: handleBulkActualize,
      export: async () => ({ success: true, message: "Export started" }),
      approve: async () => ({ success: true, message: "Approved" }),
      assign: async () => ({ success: true, message: "Assigned" }),
    },
    enableUndo: true,
    maxSelection: 50, // Limit for performance
    toastMessages: {
      success: tBulk("success") || "Action completed successfully",
      error: tBulk("error") || "Action failed",
      maxSelectionReached:
        tBulk("maxSelection") || "Maximum selection limit reached",
      noItemsSelected: tBulk("noSelection") || "No items selected",
    },
  });

  // Table columns configuration - 13 Figma columns
  const columns: PropertyTableColumn[] = useMemo(
    () => [
      {
        key: "index",
        label: tSearch("search.table.columns.id") || "#",
        width: 60,
        align: "center",
        render: (value, item, index) => (
          <span className="text-sm font-medium text-gray-600">{index + 1}</span>
        ),
      },
      {
        key: "photo",
        label: tSearch("search.table.columns.photo") || "Фото",
        width: 80,
        align: "center",
        render: (value, item) => {
          const PropertyService = propertyService.constructor as any;
          const imageUrl = PropertyService.getTableImage(item);
          return imageUrl ? (
            <Image
              src={imageUrl}
              alt={item.title || ""}
              className="w-full h-[50px] object-cover"
              fallbackSrc="/assets/img/property-placeholder.jpg"
            />
          ) : (
            <div className="w-full h-[50px] flex items-center justify-center">
              <span className="text-xs">
                {safeText(tSearch("search.noPhoto"), "—")}
              </span>
            </div>
          );
        },
      },
      {
        key: "createdAt",
        label: tSearch("search.table.columns.updated") || "Обновлено",
        width: 110,
        align: "start",
        sortable: true,
        render: (value, item) => (
          <span>
            {item.updatedAt
              ? formatDate(item.updatedAt)
              : item.createdAt
                ? formatDate(item.createdAt)
                : "—"}
          </span>
        ),
      },
      {
        key: "customId",
        label: tSearch("search.table.columns.customId") || "ID",
        width: 100,
        align: "start",
        render: (value, item) => {
          const PropertyService = propertyService.constructor as any;
          const slug = PropertyService.getPropertySlug(item);
          const id = PropertyService.getDisplayId(item);
          if (!slug && !id) return null;
          return (
            <a
              href={slug ? `/developers/${slug}` : `/developers/${id}`}
              className="table-link"
            >
              #{id}
            </a>
          );
        },
      },
      {
        key: "location",
        label: tSearch("search.table.columns.address") || "Адрес",
        width: 250,
        align: "start",
        render: (value, item) => (
          <div className="space-y-1">
            <p className="line-clamp-2">
              {item.title ||
                tSearch("search.noTitle") ||
                t("noTitle") ||
                "Без названия"}
            </p>
            <p className="line-clamp-1">
              {(item as any).fullAddress ||
                [
                  item.location?.city,
                  item.location?.district,
                  item.location?.microdistrict,
                ]
                  .filter(Boolean)
                  .join(", ")}
            </p>
          </div>
        ),
      },
      {
        key: "rooms",
        label: tSearch("search.table.columns.rooms") || "Комнаты",
        width: 80,
        align: "center",
        render: (value, item) => <span>{item.rooms || "—"}</span>,
      },
      {
        key: "area",
        label: tSearch("search.table.columns.area") || "Площадь",
        width: 100,
        align: "center",
        sortable: true,
        render: (value, item) => (
          <span>{item.area ? formatArea(item.area) : "—"}</span>
        ),
      },
      {
        key: "floor",
        label: tSearch("search.table.columns.floor") || "Этаж",
        width: 120,
        align: "center",
        render: (value, item) => {
          const PropertyService = propertyService.constructor as any;
          return <span>{PropertyService.getFloorDisplay(item)}</span>;
        },
      },
      {
        key: "price",
        label: tSearch("search.table.columns.price") || "Цена",
        width: 150,
        align: "end",
        sortable: true,
        render: (value, item) => {
          // Prefer server-side converted prices (min/max for PRIMARY)
          const convertedPricesMin =
            item.pricing?.convertedPricesMin || item.pricing?.convertedPrices;
          const convertedPricesMax = item.pricing?.convertedPricesMax;

          return (
            <Amount
              convertedPrices={convertedPricesMin || convertedPricesMax}
              variant={AmountVariant.TABLE}
            />
          );
        },
      },
      {
        key: "assignedAgent",
        label: safeText(
          tSearch("search.table.columns.assignedAgent"),
          "Ответ. агент",
        ),
        width: 160,
        align: "start",
        render: (value, item) => {
          const PropertyService = propertyService.constructor as any;
          if (!PropertyService.hasAssignedAgent(item)) {
            return (
              <div className="flex items-center gap-2 text-orange-600">
                <WarningCircleIcon />
                <span className="text-sm">Не назначен</span>
              </div>
            );
          }

          const agentName = (item as any).assignedAgentName;
          const agentPhone = (item as any).assignedAgentPhone;
          const avatarUrl = item.realtorFields?.assignedRealtor?.avatarUrl;

          return (
            <div
              className="flex items-center gap-2 min-w-0"
              title={`${agentName}${agentPhone ? " • " + agentPhone : ""}`}
            >
              {avatarUrl ? (
                <Image
                  src={avatarUrl}
                  alt={agentName}
                  className="w-5 h-5 rounded-full object-cover"
                />
              ) : (
                <UserCircleIcon className="text-[#616161]" />
              )}
              <span className="truncate">{agentName}</span>
            </div>
          );
        },
      },
      {
        key: "ownerPhone",
        label:
          tSearch("search.table.columns.ownerPhone") || "Телефон владельца",
        width: 140,
        align: "start",
        render: (value, item) => {
          const PropertyService = propertyService.constructor as any;
          const maskedPhone = PropertyService.getMaskedOwnerPhone(item);
          return maskedPhone ? (
            <span title="Номер скрыт частично">{maskedPhone}</span>
          ) : (
            <span>—</span>
          );
        },
      },
      {
        key: "publicationStatus",
        label: tSearch("search.table.columns.exclusive") || "Эксклюзив",
        width: 120,
        align: "center",
        render: (value, item) => {
          const status = item.publicationStatus;
          if (!status) return <span>—</span>;

          // Use configuration-based status mapping (to be defined in config)
          const statusConfig = {
            published: { color: "success" as const, key: "published" },
            active: { color: "success" as const, key: "active" },
            draft: { color: "warning" as const, key: "draft" },
            pending: { color: "warning" as const, key: "pending" },
            archived: { color: "danger" as const, key: "archived" },
            suspended: { color: "danger" as const, key: "suspended" },
            rejected: { color: "danger" as const, key: "rejected" },
          };

          const config = statusConfig[status as keyof typeof statusConfig];
          const color = config?.color || "default";
          const translationKey = config?.key || status;

          return (
            <Chip
              size="sm"
              color={color}
              variant="flat"
            >
              {safeText(
                tSearch(`search.publicationStatus.${translationKey}`),
                status,
              )}
            </Chip>
          );
        },
      },
      {
        key: "lastCall",
        label: tSearch("search.table.columns.lastCall") || "Последний звонок",
        width: 180,
        align: "start",
        render: (value, item) => {
          const callDate = item.realtorFields?.callDate;
          const callStatus = item.realtorFields?.callStatus;

          // Configuration-based call status mapping
          const callStatusConfig = {
            answered: { color: "success" as const, key: "answered" },
            notAnswered: { color: "warning" as const, key: "notAnswered" },
            busy: { color: "warning" as const, key: "busy" },
            noAnswer: { color: "warning" as const, key: "noAnswer" },
            wrongNumber: { color: "danger" as const, key: "wrongNumber" },
            callback: { color: "secondary" as const, key: "callback" },
            interested: { color: "success" as const, key: "interested" },
            notInterested: { color: "danger" as const, key: "notInterested" },
          };

          return (
            <div className="space-y-1">
              {callDate ? (
                <>
                  <p className="text-xs text-gray-600">
                    {formatDate(callDate)}
                  </p>
                  {callStatus && (
                    <Chip
                      size="sm"
                      color={
                        callStatusConfig[
                          callStatus as keyof typeof callStatusConfig
                        ]?.color || "secondary"
                      }
                      variant="flat"
                    >
                      {safeText(
                        tSearch(`search.callStatus.${callStatus}`),
                        callStatus || "",
                      )}
                    </Chip>
                  )}
                </>
              ) : (
                <Chip
                  size="sm"
                  color="default"
                  variant="flat"
                >
                  {safeText(
                    tSearch("search.callStatus.notCalled"),
                    "Не звонили",
                  )}
                </Chip>
              )}
            </div>
          );
        },
      },
      {
        key: "actions",
        label: tSearch("search.table.columns.actions") || "Действия",
        width: 80,
        align: "center",
        render: (value, item) => (
          <div className="flex items-center justify-center">
            <RealtorContextMenu
              propertyId={item.id || item._id || ""}
              placement="bottom-end"
              className="w-8 h-8"
              triggerClassName="w-8 h-8 min-w-8 min-h-8"
            />
          </div>
        ),
      },
    ],
    [t, tSearch, safeText],
  );

  // Handle row click
  const handleRowClick = useCallback((property: Property) => {
    // Navigate to property detail or open preview
    // TODO: Implement navigation to property detail page
  }, []);

  if (loading && properties.length === 0) {
    return (
      <div
        className={`flex items-center justify-center h-64 ${className || ""}`}
      >
        <Loader />
        <span className="ml-3 text-gray-600">
          {tSearch("search.table.loading") ||
            t("searchTexts.table.loading") ||
            "Loading..."}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`relative ${className || ""}`}
      data-testid="enhanced-search-table"
    >
      {/* Top controls row moved to SearchResultHeader; keep empty for spacing if needed */}

      {/* Virtualized Table */}
      <VirtualizedPropertyTable
        data={properties}
        columns={columns}
        selectedKeys={bulkActionsHook.selectedKeys}
        onSelectionChange={bulkActionsHook.setSelectedKeys}
        getRowKey={getPropertyKey}
        itemHeight={70}
        height={600}
        isLoading={loading}
        enableBulkActions={enableBulkActions}
        onRowClick={handleRowClick}
        virtualized={false}
        getRowClassName={(item, index) => {
          const isSelected = bulkActionsHook.selectedKeys.has(
            getPropertyKey(item),
          );
          return isSelected ? "bg-red-50 border-red-200" : "";
        }}
        emptyContent={
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-600 text-lg mb-2">
              {tSearch("search.table.noResults") ||
                t("tableNoResults") ||
                "No results found"}
            </p>
            <p className="text-gray-500 text-sm">
              {tSearch("search.table.noResultsDescription") ||
                t("tableNoResultsDescription") ||
                "Try adjusting your search criteria"}
            </p>
          </div>
        }
        className="rounded-lg border border-gray-200"
        aria-label={
          tSearch("search.table.ariaLabel") ||
          t("tableAriaLabel") ||
          "Property search results table"
        }
      />

      {/* Figma Pagination (internal) */}
      {!hideInternalPagination && (
        <FigmaPagination
          currentPage={pagination.currentPage}
          totalPages={Math.ceil(pagination.total / pagination.pageSize)}
          onPageChange={pagination.onPageChange}
          pageSize={pagination.pageSize}
          onPageSizeChange={pagination.onPageSizeChange}
          totalItems={pagination.total}
          pageSizeOptions={PAGINATION_CONFIG.pageSizeOptions}
          showPageSizeSelector={true}
          showItemInfo={true}
          maxVisiblePages={7}
        />
      )}

      {/* Bulk Actions Panel */}
      {enableBulkActions && bulkActions.length > 0 && (
        <div className="bulk-footer-right">
          <BulkActionsPanel
            selectedCount={bulkActionsHook.selectedCount}
            actions={bulkActions}
            onActionExecute={bulkActionsHook.executeAction}
            onClearSelection={bulkActionsHook.clearSelection}
            onUndo={bulkActionsHook.undoLastAction}
            canUndo={bulkActionsHook.canUndo}
            undoActionLabel={bulkActionsHook.undoActionLabel}
            isLoading={isExecutingAction}
            position="bottom"
            totalCount={pagination.total}
            compact={false}
          />
        </div>
      )}
    </div>
  );
}
